import { useEffect, useState } from "react";

// Resolves a placeholder the project's app rendered for the host to fill in — the chat's
// [data-seed-chat], the element selector's [data-seed-element-selector].
//
// The app renders in this realm, so the lookup is a plain querySelector inside its
// container. Its React tree can replace the placeholder when it re-renders (e.g. its key
// changes), leaving the first reference detached, so the lookup keeps observing and
// re-resolves instead of holding on to what it found first.

const MAX_ATTEMPTS = 30; // ~500ms at 60fps — covers slow boot / async remote load.

export const useViewSlotNode = ({ containerNode, selector }) => {

  const [targetNode, setTargetNode] = useState(null);

  useEffect(() => {

    if (!selector || !containerNode) {
      setTargetNode(null);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let observer = null;
    let currentTarget = null;

    const updateTarget = () => {
      const node = containerNode.querySelector(selector);
      if (node === currentTarget) return;
      currentTarget = node;
      setTargetNode(node);
    };

    const setupObserver = () => {
      if (observer) return;
      observer = new MutationObserver(updateTarget);
      observer.observe(containerNode, { childList: true, subtree: true });
    };

    const tryFind = () => {
      if (cancelled) return;
      if (!containerNode.isConnected) {
        attempts += 1;
        if (attempts < MAX_ATTEMPTS) requestAnimationFrame(tryFind);
        return;
      }
      updateTarget();
      setupObserver();
      if (currentTarget) return;
      attempts += 1;
      if (attempts < MAX_ATTEMPTS) requestAnimationFrame(tryFind);
    };

    tryFind();
    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      setTargetNode(null);
    };

  }, [containerNode, selector]);

  return targetNode;

}
